// 🔔 Notificador global do FlowChat
//
// PROBLEMA QUE ISSO RESOLVE:
// Toda a lógica de badge/som/notificação do chat morava dentro do ChatImproved,
// que só é montado quando o FlowChat está ABERTO (ChatContainer devolve null
// quando fechado). Ou seja: quem estava num fluxo/card não recebia absolutamente
// nada quando alguém mandava uma mensagem — nem badge, nem som, nem aviso.
//
// Este componente fica montado o tempo todo (dentro do SocketProvider, no App),
// ouve `chat_message_received` — que o backend já emite para a sala
// `empresa_<id>` a cada mensagem — e mostra o aviso no canto superior direito,
// com clique para abrir a conversa direto.
//
// Comunicação com o resto do app via CustomEvent (sem prop drilling):
//   flowchat:abrir     { channelId }  → App abre o FlowChat nessa conversa
//   flowchat:visivel   { aberto }     → ChatContainer avisa que está na tela
//   flowchat:lido      { channelId }  → ChatImproved avisa que zerou um canal
//   flowchat:naolidas  { total }      → este componente publica o total (badge do menu)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { XMarkIcon, ChatBubbleLeftRightIcon, HashtagIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { useSocket } from '../../contexts/SocketContext';

interface ChatNotifierProps {
  session?: any;
}

type TipoCanal = 'direct' | 'flow' | 'department' | 'general';

interface Aviso {
  channelId: string;
  channelName: string;
  tipo: TipoCanal;
  remetente: string;
  preview: string;
  quantidade: number;
  ts: number;
}

const CORES_AVATAR = [
  'from-blue-500 to-blue-600',
  'from-emerald-500 to-emerald-600',
  'from-rose-500 to-rose-600',
  'from-amber-500 to-amber-600',
  'from-violet-500 to-violet-600',
  'from-cyan-500 to-cyan-600',
  'from-pink-500 to-pink-600',
  'from-teal-500 to-teal-600',
];

const corDoNome = (nome: string) => {
  let hash = 0;
  for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
  return CORES_AVATAR[Math.abs(hash) % CORES_AVATAR.length];
};

// Quanto tempo cada aviso fica na tela antes de recolher para a pílula
const TEMPO_AVISO_MS = 12000;

const ChatNotifier: React.FC<ChatNotifierProps> = ({ session }) => {
  const { socket, isConnected } = useSocket();

  const usuarioId = session?.usuario?.id ? String(session.usuario.id) : '';
  const empresaId = session?.empresa?.id ? String(session.empresa.id) : '';

  // Avisos visíveis no canto (agrupados por conversa)
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  // Não-lidas por canal — alimenta o contador da pílula e o badge do menu
  const [naoLidas, setNaoLidas] = useState<Record<string, number>>({});
  // Enquanto o FlowChat está na tela, ele mesmo cuida de badge/som
  const [chatVisivel, setChatVisivel] = useState(false);
  const [pilhaExpandida, setPilhaExpandida] = useState(false);
  // Pílula dispensada fica escondida até chegar mensagem nova
  const [pilulaDispensada, setPilulaDispensada] = useState(false);

  // Canais do usuário: usados para nomear a conversa no aviso e para não
  // avisar sobre canal que não é dele (o evento vai para a empresa inteira)
  const meusCanaisRef = useRef<Map<string, { nome: string; tipo: TipoCanal }>>(new Map());
  const [meusCanaisVersao, setMeusCanaisVersao] = useState(0);
  // Nome de quem falou, por canal. A lista de canais é carregada na montagem,
  // então uma DM iniciada DEPOIS disso não está no cache e o aviso caía no
  // genérico "Mensagem direta". O evento do socket traz o nome — guardamos aqui.
  const nomesPorCanalRef = useRef<Map<string, string>>(new Map());
  const chatVisivelRef = useRef(false);
  chatVisivelRef.current = chatVisivel;

  // ─────────────────────────────────────────────────────────────
  // Carregar canais do usuário (nome + tipo). Silencioso: se falhar,
  // o aviso ainda aparece, só com um nome genérico.
  // ─────────────────────────────────────────────────────────────
  const carregarCanais = useCallback(async () => {
    if (!usuarioId || !empresaId) return;
    try {
      const res = await fetch(`/api/chat/channels?empresaId=${empresaId}&userId=${usuarioId}`);
      const data = await res.json();
      if (!data?.success || !Array.isArray(data.data)) return;
      const mapa = new Map<string, { nome: string; tipo: TipoCanal }>();
      data.data.forEach((ch: any) => {
        mapa.set(String(ch.id), { nome: ch.name || 'Conversa', tipo: (ch.type || 'general') as TipoCanal });
      });
      meusCanaisRef.current = mapa;
      setMeusCanaisVersao(v => v + 1);
    } catch {
      // sem canais em cache: o aviso usa o nome de quem mandou
    }
  }, [usuarioId, empresaId]);

  useEffect(() => { carregarCanais(); }, [carregarCanais]);

  // ─────────────────────────────────────────────────────────────
  // Semear as não-lidas ao entrar: quem recebeu DM enquanto estava fora
  // precisa ver isso assim que abre o Escalone.
  //
  // Só MENSAGEM DIRETA entra na semente. Canal/departamento sem marcador de
  // leitura conta o histórico inteiro (chat_storage.contar_nao_lidas trata
  // "nunca leu" como tudo não lido), e aí todo mundo veria "99+" permanente
  // logo no primeiro acesso. Mensagem de canal recebida AO VIVO continua
  // contando normalmente — essa é acionável.
  // ─────────────────────────────────────────────────────────────
  const buscarNaoLidas = useCallback(async () => {
    if (!usuarioId || !empresaId) return;
    try {
      const res = await fetch(`/api/chat/unread?userId=${usuarioId}&empresaId=${empresaId}`);
      const data = await res.json();
      if (!data?.success || !data.data) return;
      // O backend devolve um dicionário { channelId: count }
      const mapa: Record<string, number> = {};
      Object.entries(data.data as Record<string, number>).forEach(([channelId, count]) => {
        if (count > 0 && channelId.startsWith('dm-')) mapa[channelId] = count;
      });
      setNaoLidas(mapa);
    } catch {
      // rede instável: o socket ainda alimenta o contador
    }
  }, [usuarioId, empresaId]);

  useEffect(() => { buscarNaoLidas(); }, [buscarNaoLidas]);

  // Som curto (mesmo bipe do FlowChat, sem depender de arquivo de áudio)
  const tocarSom = useCallback(() => {
    try {
      const saved = localStorage.getItem('chat_settings');
      const settings = saved ? JSON.parse(saved) : { enabled: true, sound: true };
      if (!settings.enabled || !settings.sound) return;

      const volSaved = localStorage.getItem('chat_sound_volume');
      const volume = volSaved ? parseInt(volSaved) / 100 : 0.7;

      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(Math.max(0.01, volume * 0.5), ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch {
      // som indisponível não pode quebrar a notificação
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // A mensagem é para mim?
  // DM: o id do canal é `dm-<idA>-<idB>` (ids ordenados), então basta
  //     conter o meu id. Canal/departamento: só avisa se ele está na
  //     minha lista de canais.
  // ─────────────────────────────────────────────────────────────
  const mensagemEhMinha = useCallback((channelId: string): boolean => {
    if (channelId.startsWith('dm-')) {
      return !!usuarioId && channelId.includes(usuarioId);
    }
    if (meusCanaisRef.current.size === 0) return true; // lista ainda não carregou
    return meusCanaisRef.current.has(channelId);
  }, [usuarioId]);

  // ─────────────────────────────────────────────────────────────
  // Socket: a mensagem chega em qualquer tela, porque o SocketProvider
  // entra na sala `empresa_<id>` no login e o backend emite lá.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket || !isConnected || !usuarioId) return;

    const handler = (data: any) => {
      const channelId = String(data?.channelId || '');
      if (!channelId) return;
      if (String(data?.userId || '') === usuarioId) return; // eco da minha própria mensagem
      if (!mensagemEhMinha(channelId)) return;

      // Canal silenciado nas configurações do FlowChat
      try {
        const mudos = JSON.parse(localStorage.getItem(`mutedChannels_${usuarioId}`) || '[]');
        if (Array.isArray(mudos) && mudos.includes(channelId)) return;
      } catch { /* localStorage corrompido não pode travar o aviso */ }

      const remetente = data?.userName || 'Alguém';
      const ehDM = channelId.startsWith('dm-');

      // Em DM o nome da conversa é o nome de quem falou. Guardamos para o aviso
      // e para a lista de pendentes não caírem no genérico "Mensagem direta".
      if (ehDM && data?.userName) {
        nomesPorCanalRef.current.set(channelId, data.userName);
      }
      // Conversa que ainda não está no cache: buscar a lista de novo para
      // resolver nome e tipo pelo servidor (DM criada depois que o app abriu).
      if (!meusCanaisRef.current.has(channelId)) {
        carregarCanais();
      }

      // Contador sempre sobe, mesmo com o chat aberto (o badge do menu usa isso)
      setNaoLidas(prev => ({ ...prev, [channelId]: (prev[channelId] || 0) + 1 }));
      setPilulaDispensada(false); // mensagem nova traz a pílula de volta

      // Com o FlowChat na tela, quem avisa é o próprio chat (abas, som, badge)
      if (chatVisivelRef.current) return;

      const info = meusCanaisRef.current.get(channelId);
      const tipo: TipoCanal = ehDM ? 'direct' : (info?.tipo || 'general');
      const channelName = ehDM ? (info?.nome || remetente) : (info?.nome || 'Canal');
      const preview = String(data?.content || '').trim() || 'Enviou uma mensagem';

      setAvisos(prev => {
        const existente = prev.find(a => a.channelId === channelId);
        const atualizado: Aviso = {
          channelId,
          channelName,
          tipo,
          remetente,
          preview,
          quantidade: (existente?.quantidade || 0) + 1,
          ts: Date.now()
        };
        // Conversa mais recente sempre no topo, máximo de 4 na tela
        return [atualizado, ...prev.filter(a => a.channelId !== channelId)].slice(0, 4);
      });

      tocarSom();

      // Notificação do sistema operacional (se o usuário permitiu)
      try {
        const chatSettings = JSON.parse(localStorage.getItem('chat_settings') || '{"enabled":true,"desktop":true}');
        if (chatSettings.enabled && chatSettings.desktop && 'Notification' in window && Notification.permission === 'granted') {
          new Notification(`💬 ${remetente}`, {
            body: preview.slice(0, 120),
            icon: '/favicon.ico',
            tag: `chat-${channelId}`
          });
        }
      } catch { /* notificação do SO é bônus, nunca obrigatória */ }
    };

    socket.on('chat_message_received', handler);
    return () => { socket.off('chat_message_received', handler); };
  }, [socket, isConnected, usuarioId, mensagemEhMinha, tocarSom, meusCanaisVersao, carregarCanais]);

  // ─────────────────────────────────────────────────────────────
  // Eventos do FlowChat
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onVisivel = (e: Event) => {
      const aberto = !!(e as CustomEvent).detail?.aberto;
      setChatVisivel(aberto);
      if (aberto) {
        // Chat na tela: os avisos flutuantes perdem a função
        setAvisos([]);
        setPilhaExpandida(false);
      } else {
        // Fechou: reconcilia o contador com o servidor
        buscarNaoLidas();
        carregarCanais();
      }
    };

    const onLido = (e: Event) => {
      const channelId = String((e as CustomEvent).detail?.channelId || '');
      if (!channelId) return;
      setNaoLidas(prev => {
        if (!prev[channelId]) return prev;
        const proximo = { ...prev };
        delete proximo[channelId];
        return proximo;
      });
      setAvisos(prev => prev.filter(a => a.channelId !== channelId));
    };

    window.addEventListener('flowchat:visivel', onVisivel);
    window.addEventListener('flowchat:lido', onLido);
    return () => {
      window.removeEventListener('flowchat:visivel', onVisivel);
      window.removeEventListener('flowchat:lido', onLido);
    };
  }, [buscarNaoLidas, carregarCanais]);

  // Recolher avisos antigos (o contador continua na pílula)
  useEffect(() => {
    if (avisos.length === 0 || pilhaExpandida) return;
    const timer = setInterval(() => {
      const limite = Date.now() - TEMPO_AVISO_MS;
      setAvisos(prev => prev.filter(a => a.ts > limite));
    }, 1000);
    return () => clearInterval(timer);
  }, [avisos.length, pilhaExpandida]);

  const total = useMemo(
    () => Object.values(naoLidas).reduce((soma, n) => soma + (n || 0), 0),
    [naoLidas]
  );

  // Publica o total para o badge do menu lateral
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('flowchat:naolidas', { detail: { total } }));
  }, [total]);

  // Leva o nome junto: se a DM ainda não existir na lista da API, o chat monta
  // o canal na hora com o nome certo em vez de cair no Geral.
  const abrirConversa = (channelId?: string, nome?: string) => {
    const nomeResolvido = channelId
      ? (nome || meusCanaisRef.current.get(channelId)?.nome || nomesPorCanalRef.current.get(channelId))
      : undefined;
    window.dispatchEvent(new CustomEvent('flowchat:abrir', {
      detail: { channelId, nome: nomeResolvido }
    }));
    setAvisos([]);
    setPilhaExpandida(false);
  };

  // Conversas pendentes para a lista da pílula expandida
  const conversasPendentes = useMemo(() => {
    return Object.entries(naoLidas)
      .filter(([, count]) => count > 0)
      .map(([channelId, count]) => {
        const info = meusCanaisRef.current.get(channelId);
        const ehDM = channelId.startsWith('dm-');
        // Ordem de preferência: nome do canal (API) → nome de quem falou (socket)
        // → genérico. O do socket cobre DM iniciada depois que o app carregou.
        const nome = info?.nome
          || nomesPorCanalRef.current.get(channelId)
          || (ehDM ? 'Mensagem direta' : 'Canal');
        return {
          channelId,
          count,
          nome,
          tipo: (ehDM ? 'direct' : info?.tipo || 'general') as TipoCanal
        };
      })
      .sort((a, b) => b.count - a.count);
    // meusCanaisVersao entra na dependência para renomear quando a lista chega
  }, [naoLidas, meusCanaisVersao]);

  const mostraPilula = total > 0 && !pilulaDispensada;
  if (chatVisivel || (avisos.length === 0 && !mostraPilula)) return null;

  const IconeTipo = ({ tipo }: { tipo: TipoCanal }) => {
    if (tipo === 'direct') return <ChatBubbleLeftRightIcon className="h-3 w-3" />;
    if (tipo === 'department') return <BuildingOfficeIcon className="h-3 w-3" />;
    return <HashtagIcon className="h-3 w-3" />;
  };

  return (
    // top-16 e não top-0: os cabeçalhos das telas (Kanban, CRM, Tarefas) usam a
    // faixa do topo — descer 4rem evita cobrir "Nova Etapa"/engrenagem.
    // pointer-events-none no container: só os cartões capturam clique.
    <div className="fixed top-16 right-3 sm:right-4 z-[9997] pointer-events-none flex flex-col items-end gap-2 w-[min(92vw,330px)]">
      {avisos.map((aviso) => (
        <button
          key={aviso.channelId}
          onClick={() => abrirConversa(aviso.channelId, aviso.tipo === 'direct' ? aviso.remetente : aviso.channelName)}
          className="pointer-events-auto w-full text-left bg-white rounded-xl border border-slate-200 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.3)] overflow-hidden hover:border-emerald-300 hover:shadow-[0_16px_36px_-8px_rgba(0,0,0,0.35)] transition-all animate-[slideIn_0.2s_ease-out]"
        >
          <div className="flex items-start gap-2.5 p-3">
            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${corDoNome(aviso.remetente)} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>
              {aviso.remetente.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-bold text-slate-900 truncate">{aviso.remetente}</span>
                {aviso.quantidade > 1 && (
                  <span className="text-[9px] font-bold text-white bg-rose-500 rounded-full px-1.5 py-0.5 flex-shrink-0">
                    {aviso.quantidade}
                  </span>
                )}
              </div>
              {aviso.tipo !== 'direct' && (
                <div className="flex items-center gap-1 text-[10px] text-slate-400 font-semibold">
                  <IconeTipo tipo={aviso.tipo} />
                  <span className="truncate">{aviso.channelName}</span>
                </div>
              )}
              <p className="text-[11px] text-slate-600 line-clamp-2 mt-0.5 break-words">{aviso.preview}</p>
              <span className="text-[10px] font-semibold text-emerald-600">Clique para responder</span>
            </div>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setAvisos(prev => prev.filter(a => a.channelId !== aviso.channelId));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.stopPropagation(); }}
              className="text-slate-300 hover:text-slate-500 transition-colors flex-shrink-0 p-0.5"
              title="Dispensar aviso"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </span>
          </div>
          <div className="h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-500" />
        </button>
      ))}

      {/* Pílula de acesso rápido — fica enquanto houver mensagem sem ler */}
      {mostraPilula && (
        <div className="pointer-events-auto flex flex-col items-end gap-1.5 w-full">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPilhaExpandida(!pilhaExpandida)}
              className="flex items-center gap-2 pl-2.5 pr-3 py-1.5 bg-slate-900 text-white rounded-full shadow-lg hover:bg-slate-800 transition-colors"
              title="Mensagens não lidas no FlowChat"
            >
              <span className="relative flex items-center justify-center">
                <span className="text-sm leading-none">💬</span>
                <span className="absolute -top-1.5 -right-2 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center">
                  {total > 99 ? '99+' : total}
                </span>
              </span>
              <span className="text-[11px] font-bold whitespace-nowrap">
                {total === 1 ? '1 nova mensagem' : `${total} novas mensagens`}
              </span>
            </button>
            <button
              onClick={() => { setPilulaDispensada(true); setPilhaExpandida(false); }}
              className="p-1 text-slate-400 hover:text-slate-600 bg-white/80 hover:bg-white rounded-full border border-slate-200 shadow-sm transition-colors"
              title="Esconder até chegar mensagem nova (o badge do menu continua)"
            >
              <XMarkIcon className="h-3 w-3" />
            </button>
          </div>

          {pilhaExpandida && (
            <div className="w-full bg-white rounded-xl border border-slate-200 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.3)] overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">FlowChat</span>
                <button
                  onClick={() => abrirConversa()}
                  className="text-[10px] font-bold text-emerald-600 hover:text-emerald-700"
                >
                  Abrir chat
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto py-1">
                {conversasPendentes.map(conversa => (
                  <button
                    key={conversa.channelId}
                    onClick={() => abrirConversa(conversa.channelId, conversa.nome)}
                    className="w-full px-3 py-2 flex items-center gap-2.5 text-left hover:bg-emerald-50 transition-colors"
                  >
                    <span className="text-slate-400 flex-shrink-0"><IconeTipo tipo={conversa.tipo} /></span>
                    <span className="flex-1 text-xs font-semibold text-slate-800 truncate">{conversa.nome}</span>
                    <span className="bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center flex-shrink-0">
                      {conversa.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatNotifier;
